import React, { useState } from 'react';
import { 
    TextField, 
    Button, 
    Alert, 
    Box, 
    Typography, 
    Paper,
    Divider,
    CircularProgress 
} from '@mui/material';
import { LockReset as LockResetIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../config';
import { t } from '../i18n/useTranslation';

const ChangePasswordForm = () => {
    // State for form inputs
    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    // State for UI feedback
    const [status, setStatus] = useState({ type: '', message: '' });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: t('security.loading') || '' });

        if (formData.newPassword !== formData.confirmPassword) {
            setStatus({ type: 'error', message: t('security.errorMatch') });
            return;
        }
        if (formData.newPassword.length < 6) {
            setStatus({ type: 'error', message: t('security.errorLength') });
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/Auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({
                    oldPassword: formData.oldPassword,
                    newPassword: formData.newPassword,
                    confirmPassword: formData.confirmPassword
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to update password.');
            }

            setStatus({ 
                type: 'success', 
                message: t('security.success')  
            });
            // Clear sensitive fields
            setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });

        } catch (error) {
            setStatus({ type: 'error', message: error.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper elevation={0} sx={{ p: 4, maxWidth: 500, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LockResetIcon sx={{ color: '#a8851d', mr: 1, fontSize: 30 }} />
                <Typography variant="h5" sx={{ color: '#001E3C', fontWeight: 600 }}>
                    {t('security.title')}
                </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('security.subtitle')}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 3 }}>
                    {status.message}
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <TextField
                    fullWidth
                    label={t('security.currentPassword')}
                    type="password"
                    name="oldPassword"
                    value={formData.oldPassword}
                    onChange={handleChange}
                    margin="normal"
                    required
                    sx={{ mb: 2 }}
                />
                
                <TextField
                    fullWidth
                    label={t('security.newPassword')}
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    margin="normal"
                    required
                    helperText={t('security.helperText')}
                />

                <TextField
                    fullWidth
                    label={t('security.confirmPassword')}
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    margin="normal"
                    required
                    sx={{ mb: 3 }}
                />

                <Button 
                    type="submit" 
                    variant="contained" 
                    fullWidth
                    disabled={loading}
                    sx={{ 
                        bgcolor: '#a8851d', 
                        py: 1.5,
                        fontWeight: 'bold',
                        '&:hover': { bgcolor: '#8c6e18' }
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : t('security.updateButton')}
                </Button>
            </form>
        </Paper>
    );
};

export default ChangePasswordForm;